import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";
import { Sparkles, X } from "lucide-react";

const PER_PAGE = 8;

export default function Bundles() {
  const { addItem } = useCart();
  const { format } = useCurrency();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(PER_PAGE);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/bundles")
      .then((r) => { if (alive) setBundles(r.data || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const outNames = (b) =>
    (b.items || []).filter((i) => typeof i.stock === "number" && i.stock < 1).map((i) => i.name);
  const isSoldOut = (b) => outNames(b).length > 0;

  const openDetail = (b) => {
    setActive(b);
    setDetail(null);
    setDetailLoading(true);
    api
      .get("/bundles/" + b.id)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(b))
      .finally(() => setDetailLoading(false));
  };

  const addBundle = (b) => {
    const names = outNames(b);
    if (names.length) { toast.error("Sold out: " + names.join(", ")); return; }
    const stocks = (b.items || []).map((i) => (typeof i.stock === "number" ? i.stock : 9999));
    const stock = stocks.length ? Math.min.apply(null, stocks) : 1;
    addItem({
      id: b.id, name: b.name, price: b.price, image: b.image,
      currency: "JMD", sale_price: 0, stock: stock > 0 ? stock : 1,
      is_bundle: true, bundle_items: b.items,
    });
    toast.success(b.name + " added to cart");
  };

  const SkeletonCard = () => (
    <div className="flex flex-col rounded-2xl border-2 border-black/10 bg-white p-4 animate-pulse">
      <div className="h-40 w-full rounded-xl bg-gray-200" />
      <div className="mt-4 h-5 w-3/4 rounded bg-gray-200" />
      <div className="mt-2 h-4 w-1/2 rounded bg-gray-100" />
      <div className="mt-4 h-9 w-full rounded-full bg-gray-200" />
    </div>
  );

  const shown = bundles.slice(0, visible);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="flex items-center gap-2 text-3xl font-extrabold text-pink-500" style={{ fontFamily: "Fredoka, sans-serif" }}>
        <Sparkles className="text-yellow-400" /> Bundle Deals
      </h1>
      <p className="mt-2 text-gray-600">Save when you buy our hand-picked bundles. Tap a bundle to see what is inside.</p>

      {loading ? (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : bundles.length === 0 ? (
        <p className="mt-10 text-center text-gray-500">No bundles yet. Check back soon.</p>
      ) : (
        <>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {shown.map((b) => {
              const sold = isSoldOut(b);
              return (
                <div key={b.id} className="flex flex-col rounded-2xl border-2 border-black bg-white p-4 shadow-[4px_4px_0_0_rgba(0,0,0,0.85)]">
                  <button onClick={() => openDetail(b)} className="text-left">
                    {b.image ? (
                      <img src={b.image} alt={b.name} className="h-40 w-full rounded-xl object-cover" loading="lazy" />
                    ) : (
                      <div className="flex h-40 items-center justify-center rounded-xl bg-pink-50 text-pink-300"><Sparkles size={40} /></div>
                    )}
                    <h3 className="mt-3 font-bold leading-tight" style={{ fontFamily: "Fredoka, sans-serif" }}>{b.name}</h3>
                    <p className="mt-1 text-sm text-gray-500">{(b.items || []).length} items · tap to view</p>
                  </button>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-lg font-extrabold text-pink-500">{format(b.price)}</span>
                    {b.compare_at_price > b.price && <span className="text-sm text-gray-400 line-through">{format(b.compare_at_price)}</span>}
                  </div>
                  {b.savings > 0 && <span className="mt-1 inline-block w-fit rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Save {format(b.savings)}</span>}
                  {sold ? (
                    <button disabled className="mt-3 cursor-not-allowed rounded-full bg-gray-200 py-2 font-bold text-gray-500">Sold out</button>
                  ) : (
                    <button onClick={() => addBundle(b)} className="mt-3 rounded-full bg-pink-500 py-2 font-bold text-white hover:bg-pink-600">ADD TO CART</button>
                  )}
                </div>
              );
            })}
          </div>
          {visible < bundles.length && (
            <div className="mt-8 text-center">
              <button onClick={() => setVisible((v) => v + PER_PAGE)} className="rounded-full border-2 border-black px-6 py-2 font-bold hover:bg-gray-50">Load more bundles</button>
            </div>
          )}
        </>
      )}

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setActive(null)}>
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h2 className="text-2xl font-extrabold uppercase text-pink-500" style={{ fontFamily: "Fredoka, sans-serif" }}>{active.name}</h2>
              <button onClick={() => setActive(null)} className="rounded-full p-1 hover:bg-gray-100"><X /></button>
            </div>
            <p className="mt-1 text-gray-600">{(active.items || []).map((i) => i.name).join(", ")}</p>
            <div className="mt-3 flex items-center gap-3">
              <span className="text-2xl font-extrabold text-pink-500">{format(active.price)}</span>
              {active.compare_at_price > active.price && <span className="text-gray-400 line-through">{format(active.compare_at_price)}</span>}
              {active.savings > 0 && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-sm font-semibold text-emerald-700">Save {format(active.savings)}</span>}
            </div>

            <p className="mt-5 font-bold" style={{ fontFamily: "Fredoka, sans-serif" }}>What is inside ({(active.items || []).length} items):</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {detailLoading
                ? (active.items || []).map((_, i) => (
                    <div key={i} className="rounded-2xl border-2 border-black/10 p-3 animate-pulse">
                      <div className="h-28 w-full rounded-lg bg-gray-200" />
                      <div className="mt-2 h-4 w-3/4 rounded bg-gray-200" />
                      <div className="mt-1 h-3 w-1/3 rounded bg-gray-100" />
                    </div>
                  ))
                : ((detail && detail.items) || active.items || []).map((i, idx) => {
                    const sold = typeof i.stock === "number" && i.stock < 1;
                    return (
                      <div key={idx} className="rounded-2xl border-2 border-black p-3">
                        {i.image ? (
                          <img src={i.image} alt={i.name} className="h-28 w-full rounded-lg object-cover" loading="lazy" />
                        ) : (
                          <div className="flex h-28 items-center justify-center rounded-lg bg-pink-50 text-pink-300"><Sparkles size={28} /></div>
                        )}
                        <p className="mt-2 text-sm font-semibold leading-tight">{i.name}</p>
                        <p className="text-xs text-gray-500">{format(i.price)}</p>
                        {sold && <span className="mt-1 inline-block rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-600">Sold out</span>}
                      </div>
                    );
                  })}
            </div>

            {isSoldOut(active) && (
              <p className="mt-3 text-sm font-semibold text-red-600">Sold out: {outNames(active).join(", ")} out of stock.</p>
            )}

            {isSoldOut(active) ? (
              <button disabled className="mt-5 w-full cursor-not-allowed rounded-full bg-gray-200 py-3 font-bold text-gray-500">Sold out</button>
            ) : (
              <button onClick={() => { addBundle(active); setActive(null); }} className="mt-5 w-full rounded-full bg-pink-500 py-3 font-bold text-white hover:bg-pink-600">ADD BUNDLE TO CART</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
